import * as THREE from 'three';
import { BaseScene } from './base-scene';

/**
 * Configuration for portal scene
 */
export interface PortalSceneConfig {
  depth?: number;              // How deep the tunnel goes
  tunnelColor?: number;        // Color of tunnel walls
  backgroundColor?: number;    // Color at the end of tunnel
  cardWidth?: number;          // Width of the business card
  cardHeight?: number;         // Height of the business card
  showGrid?: boolean;          // Show grid at bottom of portal
  animate?: boolean;           // Animate the portal effect
  glowIntensity?: number;      // Edge glow effect
  debugHelpers?: boolean;      // Show debug helpers (axes on walls)
  enableClipping?: boolean;    // Enable clipping to card boundary (default: true)
  debugSphere?: boolean;       // Show a test sphere in the tunnel for clipping checks
  defaultRotation?: THREE.Euler;  // Default rotation applied to the scene root
  defaultScale?: THREE.Vector3;   // Default scale applied to the scene root
}

/**
 * Portal/Tunnel Scene
 * Makes the tracking image look like a rectangular tunnel into another dimension
 */
export class PortalScene extends BaseScene {
  private config: PortalSceneConfig;
  private portalBottom: THREE.Mesh | null = null;
  private gridHelper: THREE.GridHelper | null = null;
  private edgeGlow: THREE.Line | null = null;
  private startTime: number = Date.now();
  private localClippingPlanes: THREE.Plane[] = [];
  private clippingPlanes: THREE.Plane[] = [];
  private previousClippingState: boolean | null = null;
  private readonly normalMatrix: THREE.Matrix3 = new THREE.Matrix3();
  private planeHelpers: THREE.PlaneHelper[] = [];
  private debugHelperSize: number = 1;
  private debugSphere: THREE.Mesh | null = null;
  private stencilMask: THREE.Mesh | null = null;
  private readonly defaultRotation?: THREE.Euler;
  private readonly defaultScale?: THREE.Vector3;

  constructor(config: PortalSceneConfig = {}) {
    super({
      id: 'portal-tunnel',
      name: 'Portal Tunnel',
      description: 'Rectangular tunnel/portal effect on tracking image'
    });
    this.config = config;
    this.defaultRotation = config.defaultRotation?.clone();
    this.defaultScale = config.defaultScale?.clone();
  }

  init(parent: THREE.Object3D, renderer: THREE.WebGLRenderer): void {
    if (this.initialized) {
      console.warn('PortalScene already initialized');
      return;
    }

    parent.add(this.root);
    this.renderer = renderer;

    // Apply default transform before building any geometry
    if (this.defaultRotation) {
      this.root.rotation.copy(this.defaultRotation);
    }

    if (this.defaultScale) {
      this.root.scale.copy(this.defaultScale);
    }

    // Configuration defaults
    const depth = this.config.depth ?? 0.2;
    const cardWidth = this.config.cardWidth ?? 0.85;  // Business card aspect ratio
    const cardHeight = this.config.cardHeight ?? 0.55;
    const tunnelColor = this.config.tunnelColor ?? 0x1a1a2e;
    const bgColor = this.config.backgroundColor ?? 0x0f0f1e;
    const glowIntensity = this.config.glowIntensity ?? 0.5;
    const enableClipping = this.config.enableClipping ?? true;
    this.debugHelperSize = Math.max(cardWidth, cardHeight, depth) * 1.5;

    console.log('🎪 Portal Scene Configuration:');
    console.log(`   Card dimensions: ${cardWidth} x ${cardHeight}`);
    console.log(`   Depth: ${depth}`);
    console.log(`   Default rotation:`, this.defaultRotation);
    console.log(`   Default scale:`, this.defaultScale);

    // Create clipping planes to constrain portal to card boundary
    if (enableClipping) {
      this.localClippingPlanes = this.createClippingPlanes(cardWidth, cardHeight);
      this.clippingPlanes = this.localClippingPlanes.map(() => new THREE.Plane());

      if (this.renderer) {
        this.previousClippingState = this.renderer.localClippingEnabled;
        if (!this.renderer.localClippingEnabled) {
          // Enable local clipping so only the interior of the tunnel renders
          this.renderer.localClippingEnabled = true;
        }
      }

      parent.updateMatrixWorld(true);
      this.root.updateMatrixWorld(true);
      this.syncClippingPlanes();
    }

    this.createStencilMask(cardWidth, cardHeight);

    // Create tunnel walls (rectangular tube)
    this.createTunnelWalls(cardWidth, cardHeight, depth, tunnelColor);

    // Create portal bottom (end of tunnel)
    this.createPortalBottom(cardWidth, cardHeight, depth, bgColor);

    if (this.config.debugSphere) {
      this.addDebugSphere(Math.min(cardWidth, cardHeight) * 0.25, -depth * 0.5);
    }

    // Add edge glow effect
    if (glowIntensity > 0) {
      this.createEdgeGlow(cardWidth, cardHeight, glowIntensity);
    }

    // Add grid at bottom if requested
    if (this.config.showGrid) {
      this.addGridAtBottom(depth, cardWidth);
    }

    // Add atmospheric lighting
    this.setupLighting(depth);

    // Add debug helpers if requested
    if (this.config.debugHelpers) {
      this.addDebugHelpers(cardWidth, cardHeight, depth);
    }

    this.initialized = true;
    console.log(`✅ PortalScene initialized (depth: ${depth})`);
  }

  /**
   * Create clipping planes to constrain portal rendering to card boundary
   */
  private createClippingPlanes(width: number, height: number): THREE.Plane[] {
    // Create planes forming a rectangular boundary at the card surface.
    // Normals point inward so the intersection keeps the tunnel interior.
    const planes: THREE.Plane[] = [];

    const rightPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      new THREE.Vector3(-1, 0, 0),
      new THREE.Vector3(width / 2, 0, 0)
    );
    const leftPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(-width / 2, 0, 0)
    );
    const frontPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      new THREE.Vector3(0, 0, -1),
      new THREE.Vector3(0, 0, height / 2)
    );
    const backPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(0, 0, -height / 2)
    );
    const topPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      new THREE.Vector3(0, -1, 0),
      new THREE.Vector3(0, 0, 0)
    );

    planes.push(rightPlane, leftPlane, frontPlane, backPlane, topPlane);

    return planes;
  }

  private createStencilMask(width: number, height: number): void {
    const geometry = new THREE.PlaneGeometry(width, height);
    const material = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide });
    material.colorWrite = false;
    material.depthWrite = false;
    material.depthTest = false;
    material.stencilWrite = true;
    material.stencilFunc = THREE.AlwaysStencilFunc;
    material.stencilRef = 1;
    material.stencilFuncMask = 0xff;
    material.stencilWriteMask = 0xff;
    material.stencilFail = THREE.ReplaceStencilOp;
    material.stencilZFail = THREE.ReplaceStencilOp;
    material.stencilZPass = THREE.ReplaceStencilOp;

    this.stencilMask = new THREE.Mesh(geometry, material);
    this.stencilMask.renderOrder = 0;
    this.stencilMask.rotation.x = -Math.PI / 2;
    this.stencilMask.position.set(0, 0.0005, 0);
    this.root.add(this.stencilMask);
  }

  private applyStencilTest(material: THREE.Material | THREE.Material[]): void {
    const configure = (mat: THREE.Material) => {
      mat.stencilWrite = true;
      mat.stencilFunc = THREE.EqualStencilFunc;
      mat.stencilRef = 1;
      mat.stencilFuncMask = 0xff;
      mat.stencilWriteMask = 0xff;
      mat.stencilFail = THREE.KeepStencilOp;
      mat.stencilZFail = THREE.KeepStencilOp;
      mat.stencilZPass = THREE.KeepStencilOp;
    };

    if (Array.isArray(material)) {
      material.forEach(configure);
    } else {
      configure(material);
    }
  }

  /**
   * Sync clipping planes with the portal's world transform so they follow the card.
   */
  private syncClippingPlanes(): void {
    if (this.localClippingPlanes.length === 0 || this.clippingPlanes.length === 0) {
      return;
    }

    this.root.updateMatrixWorld(true);
    this.normalMatrix.getNormalMatrix(this.root.matrixWorld);

    for (let i = 0; i < this.localClippingPlanes.length; i++) {
      this.clippingPlanes[i]
        .copy(this.localClippingPlanes[i])
        .applyMatrix4(this.root.matrixWorld, this.normalMatrix);
    }
  }

  /**
   * Visual helpers to see clipping planes when debugging.
   */
  private addClippingHelpers(): void {
    if (this.clippingPlanes.length === 0) {
      return;
    }

    const colors = [0xff4444, 0x44ff44, 0x4444ff, 0xffff44, 0xff44ff, 0x44ffff];

    this.removeClippingHelpers();

    this.clippingPlanes.forEach((plane, index) => {
      const helper = new THREE.PlaneHelper(plane, this.debugHelperSize, colors[index % colors.length]);
      helper.name = `portal-clipping-plane-${index}`;
      this.planeHelpers.push(helper);
      this.root.add(helper);
    });
  }

  private removeClippingHelpers(): void {
    if (this.planeHelpers.length === 0) {
      return;
    }

    this.planeHelpers.forEach(helper => {
      this.root.remove(helper);
      helper.geometry.dispose();
      (helper.material as THREE.Material).dispose();
    });
    this.planeHelpers = [];
  }

  /**
   * Create the tunnel walls as a rectangular tube
   */
  private createTunnelWalls(width: number, height: number, depth: number, color: number): void {
    // We'll create 4 walls (left, right, front, back)
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: color,
      side: THREE.FrontSide,
      roughness: 0.8,
      metalness: 0.2,
      clippingPlanes: this.clippingPlanes.length > 0 ? this.clippingPlanes : undefined,
      clipShadows: true,
      clipIntersection: true
    });

    this.applyStencilTest(wallMaterial);

    // Left wall (negative X side)
    const leftGeom = new THREE.PlaneGeometry(height, depth);
    const leftWall = new THREE.Mesh(leftGeom, wallMaterial);
    leftWall.position.set(-width / 2, -depth / 2, 0);
    leftWall.rotation.y = Math.PI / 2;  // Face inward (toward positive X)
    leftWall.renderOrder = 1;
    this.root.add(leftWall);

    // Right wall (positive X side)
    const rightWall = new THREE.Mesh(leftGeom.clone(), wallMaterial);
    rightWall.position.set(width / 2, -depth / 2, 0);
    rightWall.rotation.y = -Math.PI / 2;  // Face inward (toward negative X)
    rightWall.renderOrder = 1;
    this.root.add(rightWall);

    // Front wall (positive Z side)
    const frontGeom = new THREE.PlaneGeometry(width, depth);
    const frontWall = new THREE.Mesh(frontGeom, wallMaterial);
    frontWall.position.set(0, -depth / 2, height / 2);
    frontWall.rotation.x = 0;  // Face downward, toward negative Z
    frontWall.rotation.y = Math.PI;        // Flip to face inward
    frontWall.renderOrder = 1;
    this.root.add(frontWall);

    // Back wall (negative Z side)
    const backWall = new THREE.Mesh(frontGeom.clone(), wallMaterial);
    backWall.position.set(0, -depth / 2, -height / 2);
    backWall.rotation.x =0;  // Face downward, toward positive Z
    backWall.renderOrder = 1;
    this.root.add(backWall);

    console.log('🔲 Tunnel walls created');
  }

  /**
   * Create the bottom surface at the end of the tunnel
   */
  private createPortalBottom(width: number, height: number, depth: number, color: number): void {
    const geometry = new THREE.PlaneGeometry(width, height);
    const material = new THREE.MeshStandardMaterial({
      color: color,
      side: THREE.FrontSide,
      emissive: color,
      emissiveIntensity: 0.3,
      roughness: 0.5,
      metalness: 0.1,
      clippingPlanes: this.clippingPlanes.length > 0 ? this.clippingPlanes : undefined,
      clipShadows: true,
      clipIntersection: true
    });

    this.applyStencilTest(material);

    this.portalBottom = new THREE.Mesh(geometry, material);
    this.portalBottom.position.set(0, -depth, 0);
    this.portalBottom.rotation.x = -Math.PI / 2;
    this.portalBottom.renderOrder = 1;
    this.root.add(this.portalBottom);

    console.log('🌌 Portal bottom created');
  }

  private addDebugSphere(radius: number, yOffset: number): void {
    const geometry = new THREE.SphereGeometry(radius, 32, 16);
    const material = new THREE.MeshStandardMaterial({
      color: 0xffaa00,
      emissive: 0x552200,
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.7,
      roughness: 0.2,
      metalness: 0.1,
      clippingPlanes: this.clippingPlanes.length > 0 ? this.clippingPlanes : undefined,
      clipIntersection: true
    });

    this.applyStencilTest(material);

    this.debugSphere = new THREE.Mesh(geometry, material);
    this.debugSphere.position.set(0, yOffset, 0);
    this.debugSphere.renderOrder = 1;
    this.root.add(this.debugSphere);
  }

  /**
   * Create glowing edge effect around the portal entrance
   */
  private createEdgeGlow(width: number, height: number, intensity: number): void {
    // Create a frame outline
    const points: THREE.Vector3[] = [];
    const hw = width / 2;
    const hh = height / 2;

    // Rectangle outline
    points.push(new THREE.Vector3(-hw, 0, -hh));
    points.push(new THREE.Vector3(hw, 0, -hh));
    points.push(new THREE.Vector3(hw, 0, hh));
    points.push(new THREE.Vector3(-hw, 0, hh));
    points.push(new THREE.Vector3(-hw, 0, -hh));

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color: 0x00ffff,
      linewidth: 3,
      transparent: true,
      opacity: intensity,
      clippingPlanes: this.clippingPlanes.length > 0 ? this.clippingPlanes : undefined,
      clipIntersection: true
    });

    this.applyStencilTest(material);

    this.edgeGlow = new THREE.Line(geometry, material);
    this.edgeGlow.renderOrder = 2;
    this.root.add(this.edgeGlow);

    console.log('✨ Edge glow added');
  }

  /**
   * Add grid at the bottom of the portal
   */
  private addGridAtBottom(depth: number, size: number): void {
    this.gridHelper = new THREE.GridHelper(size * 2, 10, 0x00ffff, 0x004444);
    this.gridHelper.position.y = -depth;
    this.gridHelper.renderOrder = 1;
    this.applyStencilTest(this.gridHelper.material as THREE.Material | THREE.Material[]);
    this.root.add(this.gridHelper);
    console.log('📐 Grid added to portal bottom');
  }

  /**
   * Setup atmospheric lighting for the portal
   */
  private setupLighting(depth: number): void {
    // Ambient light for base visibility
    this.addAmbientLight(0x4444ff, 0.3);

    // Light from the bottom of the portal (glowing effect)
    const portalLight = new THREE.PointLight(0x0088ff, 1, depth * 2);
    portalLight.position.set(0, -depth, 0);
    this.root.add(portalLight);

    // Rim light at the entrance
    const rimLight = new THREE.PointLight(0x00ffff, 0.5, 2);
    rimLight.position.set(0, 0, 0);
    this.root.add(rimLight);

    console.log('💡 Portal lighting setup complete');
  }

  update(delta: number): void {
    if (!this.initialized) return;

    if (this.clippingPlanes.length > 0) {
      this.syncClippingPlanes();
      if (this.planeHelpers.length > 0) {
        this.planeHelpers.forEach(helper => helper.updateMatrixWorld(true));
      }
    }

    if (this.config.animate) {
      const time = (Date.now() - this.startTime) / 1000;

      // Pulse the portal bottom glow
      if (this.portalBottom) {
        const material = this.portalBottom.material as THREE.MeshStandardMaterial;
        material.emissiveIntensity = 0.3 + Math.sin(time * 2) * 0.2;
      }

      // Pulse edge glow
      if (this.edgeGlow) {
        const material = this.edgeGlow.material as THREE.LineBasicMaterial;
        material.opacity = 0.5 + Math.sin(time * 3) * 0.3;
      }

      // Rotate grid slowly
      if (this.gridHelper) {
        this.gridHelper.rotation.y += delta * 0.2;
      }
    }
  }

  dispose(): void {
    if (this.gridHelper) {
      this.root.remove(this.gridHelper);
      this.gridHelper = null;
    }

    if (this.edgeGlow) {
      this.root.remove(this.edgeGlow);
      this.edgeGlow = null;
    }

    if (this.debugSphere) {
      this.root.remove(this.debugSphere);
      this.debugSphere.geometry.dispose();
      const material = this.debugSphere.material as THREE.Material;
      material.dispose();
      this.debugSphere = null;
    }

    if (this.stencilMask) {
      this.root.remove(this.stencilMask);
      this.stencilMask.geometry.dispose();
      const material = this.stencilMask.material as THREE.Material;
      material.dispose();
      this.stencilMask = null;
    }

    if (this.portalBottom) {
      this.root.remove(this.portalBottom);
      this.portalBottom = null;
    }

    this.removeClippingHelpers();

    if (this.renderer && this.previousClippingState !== null) {
      this.renderer.localClippingEnabled = this.previousClippingState;
      this.previousClippingState = null;
    }

    this.localClippingPlanes = [];
    this.clippingPlanes = [];
    this.renderer = null;

    super.dispose();
  }

  /**
   * Add debug helpers to visualize wall orientations
   */
  private addDebugHelpers(width: number, height: number, depth: number): void {
    // Add axes helper at each wall to show orientation
    const axesSize = 0.3;

    // Left wall axes
    const leftAxes = new THREE.AxesHelper(axesSize);
    leftAxes.position.set(-width / 2, -depth / 2, 0);
    this.root.add(leftAxes);

    // Right wall axes
    const rightAxes = new THREE.AxesHelper(axesSize);
    rightAxes.position.set(width / 2, -depth / 2, 0);
    this.root.add(rightAxes);

    // Front wall axes
    const frontAxes = new THREE.AxesHelper(axesSize);
    frontAxes.position.set(0, -depth / 2, height / 2);
    this.root.add(frontAxes);

    // Back wall axes
    const backAxes = new THREE.AxesHelper(axesSize);
    backAxes.position.set(0, -depth / 2, -height / 2);
    this.root.add(backAxes);

    // Portal entrance axes
    const entranceAxes = new THREE.AxesHelper(axesSize);
    entranceAxes.position.set(0, 0, 0);
    this.root.add(entranceAxes);

    // Portal bottom axes
    const bottomAxes = new THREE.AxesHelper(axesSize);
    bottomAxes.position.set(0, -depth, 0);
    this.root.add(bottomAxes);

    // Show clipping planes to visualize the window when debugging
    this.addClippingHelpers();

    // Add a wireframe box at the card boundary to visualize stencil mask dimensions
    const boundaryGeometry = new THREE.PlaneGeometry(width, height);
    const boundaryEdges = new THREE.EdgesGeometry(boundaryGeometry);
    const boundaryLine = new THREE.LineSegments(
      boundaryEdges,
      new THREE.LineBasicMaterial({ color: 0xff00ff, linewidth: 3 })
    );
    boundaryLine.rotation.x = -Math.PI / 2;
    boundaryLine.position.set(0, 0.001, 0);  // Slightly above stencil mask
    this.root.add(boundaryLine);

    console.log(`🔍 Debug helpers added (axes at each wall, card boundary: ${width}x${height})`);
  }

  /**
   * Set portal depth dynamically
   */
  setDepth(depth: number): void {
    console.log(`🔧 Changing portal depth to ${depth} (requires re-initialization)`);
    // Note: This would require rebuilding the scene
    // For now, just log it. Full implementation would recreate geometry.
  }

  /**
   * Enable/disable animation
   */
  setAnimate(animate: boolean): void {
    this.config.animate = animate;
    console.log(`🎬 Portal animation ${animate ? 'enabled' : 'disabled'}`);
  }
}
